import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Trash2, User, Calendar } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { DealItemsManager } from "./DealItemsManager";
import {
  DealCommissionFields,
  commissionFromDeal,
  emptyDealCommission,
  toCommissionPayload,
  type DealCommissionFieldState,
} from "./DealCommissionFields";
import type { DealWithStage, DealStage } from "@/types/deal";

function formatCurrency(v: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);
}

interface WorkspaceMemberOption {
  id: string;
  display_name: string | null;
}

interface DealDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deal: DealWithStage | null;
  stages: DealStage[];
  workspaceId: string;
  memberId: string | null;
  onUpdate: (id: string, updates: Record<string, any>) => void;
  onMoveDealStage: (dealId: string, toStageId: string, memberId?: string) => void;
  onDelete: (id: string) => void;
}

export function DealDetailDrawer({ open, onOpenChange, deal, stages, workspaceId, memberId, onUpdate, onMoveDealStage, onDelete }: DealDetailDrawerProps) {
  const [title, setTitle] = useState("");
  const [manualAmount, setManualAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [lostReason, setLostReason] = useState("");
  const [amountSource, setAmountSource] = useState<"manual" | "items">("manual");
  const [ownerMemberId, setOwnerMemberId] = useState<string>("");
  const [commission, setCommission] = useState<DealCommissionFieldState>(emptyDealCommission);
  const [members, setMembers] = useState<WorkspaceMemberOption[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (!open || !workspaceId) return;
    void (async () => {
      const { data } = await supabase
        .from("workspace_members")
        .select("id, display_name")
        .eq("workspace_id", workspaceId)
        .order("display_name", { ascending: true });
      setMembers((data ?? []) as WorkspaceMemberOption[]);
    })();
  }, [open, workspaceId]);

  useEffect(() => {
    if (deal) {
      setTitle(deal.title);
      setManualAmount(String(deal.manual_amount ?? 0));
      setNotes(deal.notes ?? "");
      setExpectedDate(deal.expected_close_date ?? "");
      setLostReason(deal.lost_reason ?? "");
      setAmountSource((deal.amount_source as "manual" | "items") ?? "manual");
      setOwnerMemberId(deal.owner_member_id ?? "");
      setCommission(commissionFromDeal(deal));
    }
  }, [deal]);

  if (!deal) return null;

  const handleSave = () => {
    onUpdate(deal.id, {
      title,
      manual_amount: parseFloat(manualAmount) || 0,
      notes: notes || null,
      expected_close_date: expectedDate || null,
      lost_reason: lostReason || null,
      amount_source: amountSource,
      owner_member_id: ownerMemberId || null,
      ...toCommissionPayload(commission),
    });
    onOpenChange(false);
  };

  const statusLabel = deal.status === "won" ? "Ganho" : deal.status === "lost" ? "Perdido" : "Aberto";
  const statusColor = deal.status === "won" ? "bg-green-500" : deal.status === "lost" ? "bg-red-500" : "bg-blue-500";

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-[540px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Detalhes da Negociação</SheetTitle>
            <SheetDescription>Edite as informações e gerencie itens</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-5">
            <div className="flex items-center gap-2">
              <Badge className={`${statusColor} text-white`}>{statusLabel}</Badge>
              {deal.deal_stages && <Badge variant="outline">{deal.deal_stages.name}</Badge>}
              <span className="ml-auto text-lg font-bold">{formatCurrency(Number(deal.amount))}</span>
            </div>

            {deal.leads && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="w-4 h-4" />
                <span>{deal.leads.name}</span>
                {deal.leads.phone && <span>· {deal.leads.phone}</span>}
              </div>
            )}

            <div className="space-y-2">
              <Label>Título</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label>Estágio</Label>
              <Select value={deal.stage_id} onValueChange={v => onMoveDealStage(deal.id, v, memberId ?? undefined)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {stages.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} ({s.probability}%){s.is_closed_won ? " ✅" : s.is_closed_lost ? " ❌" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Responsável</Label>
              <Select value={ownerMemberId || "__none__"} onValueChange={v => setOwnerMemberId(v === "__none__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Selecione o responsável" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sem responsável</SelectItem>
                  {members.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.display_name || "Membro"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Fonte do Valor</Label>
                <Select value={amountSource} onValueChange={v => setAmountSource(v as "manual" | "items")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="items">Por Itens</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Valor Manual (R$)</Label>
                <Input type="number" min={0} step={0.01} value={manualAmount} onChange={e => setManualAmount(e.target.value)} disabled={amountSource !== "manual"} />
              </div>
            </div>

            <DealCommissionFields members={members} value={commission} onChange={setCommission} />

            <div className="space-y-2">
              <Label>Previsão de Fechamento</Label>
              <Input type="date" value={expectedDate} onChange={e => setExpectedDate(e.target.value)} />
            </div>

            {deal.status === "lost" && (
              <div className="space-y-2">
                <Label>Motivo da Perda</Label>
                <Textarea value={lostReason} onChange={e => setLostReason(e.target.value)} rows={2} />
              </div>
            )}

            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} />
            </div>

            <div className="flex gap-2">
              <Button className="flex-1" onClick={handleSave}>Salvar</Button>
              <Button variant="destructive" size="icon" onClick={() => setDeleteOpen(true)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            <Separator />

            <DealItemsManager dealId={deal.id} workspaceId={workspaceId} />
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir negociação?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita. Todos os itens e histórico serão removidos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { onDelete(deal.id); onOpenChange(false); setDeleteOpen(false); }}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
